import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AssignmentsService, SourceTypeEnum } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableSortHeader,
  TableRow,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { AssignmentCard } from './AssignmentCard';
import { AssignmentDrawer } from './AssignmentDrawer';
import { ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY } from './queryKeys';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20;

const QUEUE_CATEGORY = 'ProviderAssignmentQueueNames';

type ApiParams = Parameters<typeof AssignmentsService.assignmentsAssignmentList>[0];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AssignmentsList: React.FC = () => {
  useDocumentTitle('Assignments');
  const [searchParams, setSearchParams] = useSearchParams();

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get the selected queue code from URL params
  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const displayTypePrefKey = 'OrganizationAssignments';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];

  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      displayType,
    ]
  );

  // Handle queue change - update URL param
  const handleQueueChange = useCallback(
    (queueCode: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (queueCode) {
        newSearchParams.set('queue', queueCode);
      } else {
        newSearchParams.delete('queue');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  // Build query key with filters
  const queryKey = useMemo(
    () => [
      ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      ordering,
    ],
    [selectedQueueCode, search, statuses, reasons, ordering]
  );

  // Fetch assignments
  const {
    data,
    isLoading: isLoadingAssignments,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return AssignmentsService.assignmentsAssignmentList({
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
    enabled: true,
    refetchOnMount: 'always',
  });

  const assignments = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  // Set up infinite scroll
  const loadMoreAssignments = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreAssignments, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingAssignments,
  });

  // Check URL for assignment ID and open drawer if present
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setIsDrawerOpen(true);
    } else {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (assignmentId: string) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', assignmentId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    newSearchParams.delete('tab');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedIdFromUrl = searchParams.get('id');

  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    return assignments.findIndex((a) => String(a.id) === String(selectedIdFromUrl));
  }, [assignments, selectedIdFromUrl]);

  const prevAssignmentId: string | null = useMemo(() => {
    if (selectedIndex > 0) return assignments[selectedIndex - 1]?.id ?? null;
    return null;
  }, [assignments, selectedIndex]);

  const nextAssignmentId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < assignments.length - 1)
      return assignments[selectedIndex + 1]?.id ?? null;
    return null;
  }, [assignments, selectedIndex]);

  // Cache neighbors for auto-advance
  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevAssignmentId ?? null,
        next: nextAssignmentId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevAssignmentId, nextAssignmentId]);

  const cachedNeighbors = selectedIdFromUrl
    ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
    : undefined;
  const drawerPrevId: string | null =
    selectedIndex >= 0 ? prevAssignmentId : (cachedNeighbors?.prev ?? null);
  const drawerNextId: string | null =
    selectedIndex >= 0 ? nextAssignmentId : (cachedNeighbors?.next ?? null);

  // Auto-advance with deep-link guard
  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  useEffect(() => {
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;
    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) return;

    if (selectedIndex === -1 && !isLoadingAssignments) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('id');
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingAssignments,
    searchParams,
    setSearchParams,
  ]);

  return (
    <>
      <div
        className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden"
        data-testid="assignments-list"
      >
        {/* Main content area */}
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="far fa-fw fa-calendar-lines-pen tw:mr-1" />
                  Assignments
                </Heading>
              </div>

              <div className="tw:mt-4">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.Assignment}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  priorityQueueList={[QueueCodes.NEEDS_RESPONSE]}
                  onQueueChange={handleQueueChange}
                />
              </div>
            </div>

            <div className="tw:mt-1">
              <div className="tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.Assignment}
                  showStatus={true}
                  showReason={true}
                  showDisplayType={true}
                  sortOptions={[
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'created_at' },
                    { label: 'Start Date', value: 'start_on' },
                    { label: 'End Date', value: 'end_on' },
                    { label: 'Status', value: 'current_status__title' },
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__city' },
                    { label: 'Duration', value: 'total_duration' },
                    { label: 'Amount', value: 'total_payable_amount' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>

            <div className="tw:mt-6">
              {isError ? (
                <ListErrorState testId="assignments-error" />
              ) : isLoadingAssignments && assignments.length === 0 ? (
                <div className="tw:text-center tw:py-8" data-testid="assignments-loading">
                  Loading assignments...
                </div>
              ) : assignments.length === 0 ? (
                <div
                  className="tw:text-center tw:text-zinc-500 tw:py-8"
                  data-testid="assignments-empty"
                >
                  No assignments found
                  <div className="tw:italic tw:text-zinc-500 tw:text-sm">
                    Refine search criteria or check back later
                  </div>
                </div>
              ) : (
                <>
                  {assignments.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {assignments.length} of {totalCount} assignments
                    </div>
                  )}
                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {assignments.map((assignment) => {
                          const isSelected = assignment.id === selectedIdFromUrl;
                          return (
                            <button
                              key={assignment.id}
                              type="button"
                              onClick={() => handleRowClick(assignment.id)}
                              className="tw:cursor-pointer tw:block tw:w-full tw:text-left"
                              data-testid={`assignment-card-${assignment.id}`}
                            >
                              <AssignmentCard assignment={assignment} isSelected={isSelected} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <Table striped dense>
                      <TableHead>
                        <TableHeader>Code</TableHeader>

                        <TableSortHeader
                          sortKey="facility__name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Facility
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="facility__city"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Location
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="current_status__title"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Status
                        </TableSortHeader>
                        {['all', 'closed'].includes(selectedQueueCode || '') && (
                          <TableHeader>Reason</TableHeader>
                        )}
                        <TableSortHeader
                          sortKey="start_on"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Date Range
                        </TableSortHeader>
                        <TableHeader># Shifts</TableHeader>
                        <TableSortHeader
                          sortKey="total_payable_amount"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                          className="tw:!text-right"
                        >
                          Amount
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="updated_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Updated
                        </TableSortHeader>
                      </TableHead>
                      <TableBody>
                        {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                        {assignments.map((assignment) => {
                          const isSelected = assignment.id === selectedIdFromUrl;
                          const dateRange = [
                            assignment.start_on
                              ? format(parseISO(assignment.start_on), DATE_FORMAT)
                              : null,
                            assignment.end_on
                              ? format(parseISO(assignment.end_on), DATE_FORMAT)
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' - ');

                          const shift_count =
                            assignment.active_shift_count > 0
                              ? assignment.active_shift_count
                              : assignment.removed_shift_count || 0;

                          return (
                            <TableRow
                              key={assignment.id}
                              onClick={() => handleRowClick(assignment.id)}
                              className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              data-testid={`assignment-row-${assignment.id}`}
                            >
                              <TableCell className="tw:font-mono tw:text-xs">
                                {assignment.code}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const facilities =
                                    (assignment as any).metadata?.shift_facilities?.length > 0
                                      ? (assignment as any).metadata.shift_facilities
                                      : [
                                          {
                                            facility_id: assignment.facility.id,
                                            facility_name: assignment.facility.name,
                                          },
                                        ];
                                  const multi = facilities.length > 1;
                                  return (
                                    <div className="tw:flex tw:flex-col tw:gap-0.5">
                                      {facilities.map((f: any) => (
                                        <span key={f.facility_id} title={f.facility_name}>
                                          {multi && <span className="tw:mr-1">&bull;</span>}
                                          {f.facility_name}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                })()}
                                {assignment.source_type === SourceTypeEnum.ORGANIZATION && (
                                  <span
                                    className={clsx(
                                      'tw:ml-1',
                                      SOURCE_TYPE_ORGANIZATION_BADGE_STYLE
                                    )}
                                  >
                                    1099
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {getFacilityLocationDisplayValue(assignment.facility) || '--'}
                              </TableCell>
                              <TableCell>
                                <StatusPill
                                  scope={WorkflowScopes.Assignment}
                                  status={assignment.current_status}
                                />
                              </TableCell>
                              {['all', 'closed'].includes(selectedQueueCode || '') && (
                                <TableCell>{assignment.current_reason?.title || '--'}</TableCell>
                              )}
                              <TableCell>{dateRange || '--'}</TableCell>
                              <TableCell>
                                {shift_count} {shift_count === 1 ? 'shift' : 'shifts'}
                              </TableCell>
                              <TableCell className="tw:text-right">
                                {formatCurrencyPostfix(assignment.total_amount)}
                              </TableCell>
                              <TableCell>
                                {assignment.updated_at
                                  ? format(parseISO(assignment.updated_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {/* Infinite scroll loading indicator */}
                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more assignments...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <AssignmentDrawer
                key={selectedIdFromUrl}
                assignmentId={selectedIdFromUrl}
                prevId={drawerPrevId}
                nextId={drawerNextId}
                selectedQueueCode={selectedQueueCode}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
